import XRegExp from 'xregexp';
import { isPlainObject, isEmpty, isNil } from 'lodash';
import isHashOrUrl from '../isHashOrUrl';
import splitDelimitedString from '../splitDelimitedString';
import isHexColorString from '../isHexColorString';

// @note: used xregexp because we need to support unicode letters @feshchenko

/* eslint-disable */
const phoneRegExp = /^[+]*[(]{0,1}[0-9]{1,3}[)]{0,1}[-\s\./0-9]*$/;
const numberRegExp = /^[-+]?\d*([.]\d*)?$/;
const alphaNumericalRegExp = XRegExp('^[\\w\\s\\p{L}-]+$');
const ipRegexp = /^(?!0)(?!.*\.$)((1?\d?\d|25[0-5]|2[0-4]\d)(\.|$)){4}$/;
const slugRegexp = /^[a-z0-9-_]+$/;
const emailRegExp = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
/* eslint-enable */

const delimitedStringList = (value, validator) => {
  if (!value) {
    return;
  }

  const validationErrors = splitDelimitedString(value).map(validator);

  return validationErrors.find(error => !isEmpty(error));
};

export const nonEmptyObject = (errorText = 'Required') => obj => {
  return obj && !isEmpty(obj) ? undefined : errorText;
};

export const required = (errorText = 'Required') => value => {
  if (isPlainObject(value)) {
    return nonEmptyObject(errorText)(value);
  }
  return !isNil(value) && value.toString().trim() ? undefined : errorText;
};

export const phone = (errorText = 'Phone format') => value => {
  if (!value) {
    return;
  }
  return phoneRegExp.test(value) ? undefined : errorText;
};

export const anyObjectKeyRequired = (
  errorText = 'Select any option',
) => value => {
  return value && Object.values(value).some(v => v) ? undefined : errorText;
};

export const alphanumerical = (errorText = 'Alphanumerical') => value => {
  if (!value) {
    return;
  }

  return alphaNumericalRegExp.test(value) ? undefined : errorText;
};

export const maxLength = (
  limit,
  errorText = 'Maximum length exceeded',
) => value => {
  return value.length > limit ? errorText : undefined;
};

export const urlSlug = (errorText = 'Invalid slug') => value => {
  return slugRegexp.test(value) ? undefined : errorText;
};

export const email = (errorText = 'Email') => value => {
  if (!value) {
    return;
  }

  return emailRegExp.test(value) ? undefined : errorText;
};

export const numLimit = (maxCharLimit = 999) => value => {
  const errorText = `Must be less than ${maxCharLimit + 1}`;
  if (!value) {
    return;
  }

  return value <= maxCharLimit ? undefined : errorText;
};

export const numRange = (start, end) => value => {
  if (!value) {
    return;
  }

  if (value < start) {
    return `Number must be more than ${start}`;
  }

  if (value > end) {
    return `Number must be less than ${end}`;
  }

  return;
};

export const number = (
  errorText = 'Invalid format, try like this `-12.231`',
) => value => {
  if (!value) {
    return;
  }
  return numberRegExp.test(value) ? undefined : errorText;
};

export const positiveNumber = (errorText = 'Positive Number') => value => {
  if (!value) {
    return;
  }

  return value > 0 ? undefined : errorText;
};

export const emailList = (errorText = 'Email') => value =>
  delimitedStringList(value, email(errorText));

export const ip = (errorText = 'IP') => value => {
  if (!value) {
    return;
  }

  return ipRegexp.test(value) ? undefined : errorText;
};

export const ipList = (errorText = 'IP') => value =>
  delimitedStringList(value, ip(errorText));

export const unique = (existenceList, errorText = 'Unique') => value => {
  if (!existenceList) {
    return;
  }

  return existenceList.includes(value) ? errorText : undefined;
};

export const uniqueOptionLabel = (errorText = 'Label should be unique') => (
  value,
  formValue,
) => {
  const duplicatesEncountered =
    formValue.options.filter(item => item.value === value).length > 1;
  return duplicatesEncountered ? errorText : undefined;
};

export const url = (errorText = 'Please enter a URL.') => value => {
  if (!value) {
    return;
  }

  return isHashOrUrl(value) ? undefined : errorText;
};

export const uniqueList = (existenceList, errorText = 'Unique') => value =>
  delimitedStringList(value, unique(existenceList, errorText));

export const hexColor = (errorText = 'Invalid Color') => value => {
  if (!value) {
    return;
  }

  return isHexColorString(value) ? undefined : errorText;
};
